/**
 * Rechenkern buildNoteTaskDraft (Story 23.20, FR-166, FR-63, EXPERIENCE §4.22 „Aus Notiz“, AD-27) – rein: keine Datenbank,
 * keine Uhr, kein Netz. Macht aus dem Text einer internen Notiz den Entwurf einer Aufgabe: Titel, Fälligkeit, Priorität
 * und zuständige Person. Die Deutung ist die feste der Schnell-Erfassung (`parseTaskQuickEntry`, 23.14) – keine zweite
 * Deutung, keine KI. `today` (Kalendertag Europe/Berlin) und die erwähnten Personen kommen als Eingabe. Ergebnis trägt
 * `NOTE_TASK_VERSION` und die Version der Deutung.
 *
 * Regeln:
 * - Jedes volle „@Anzeigename“ einer erwähnten Person (die @-Auswahl schreibt „@Ben Test“) wird vor der Deutung durch
 *   Leerzeichen gleicher Länge ersetzt (Positionen bleiben gleich); danach deutet `parseTaskQuickEntry` den ganzen
 *   Notiztext mit Personen = nur die Erwähnten, ohne Datensatz-Erkennung und ohne Labels und Projekte (`#`/`+` bleiben Text).
 * - Zuständig = die im Text zuerst stehende erwähnte Person (voller Name oder Kurzform „@Ben“); mehrdeutige Kurzform
 *   zählt nicht. Ohne Treffer: `self` (die schreibende Person, „man selbst“), sonst `null`.
 * - Fällig und Priorität aus der Deutung; ohne Datum `today`, ohne Priorität „Normal“.
 * - Titel = erste nicht leere Zeile ohne die erkannten Teile (Datum, Priorität, @Person), Leerraum zusammengefasst,
 *   höchstens `NOTE_TASK_TITLE_MAX` Zeichen (länger: gekürzt mit „…“). Bleibt nichts übrig, gilt die erste Zeile selbst.
 */
import {
  parseTaskQuickEntry,
  TASK_QUICK_ENTRY_VERSION,
  isAmbiguous,
  type QuickEntryPerson,
  type QuickEntryPriority,
  type QuickEntryToken,
} from './task-quick-entry.ts';

export const NOTE_TASK_VERSION = 'note-task-v1';

/** Höchstlänge des vorgeschlagenen Titels (Story 23.20). */
export const NOTE_TASK_TITLE_MAX = 120;

export interface NoteTaskDraftInput {
  /** Text der Notiz. */
  body: string;
  /** Kalendertag Europe/Berlin (`YYYY-MM-DD`). */
  today: string;
  /** Erwähnte Personen der Notiz (Reihenfolge egal; zählt die Stelle im Text). */
  mentioned: readonly QuickEntryPerson[];
  /** Person, die die Aufgabe anlegt („man selbst“); `null` = niemand (dann muss der Aufrufer wählen). */
  self: string | null;
}

export interface NoteTaskDraft {
  version: string;
  /** Version der festen Deutung (`TASK_QUICK_ENTRY_VERSION`). */
  quickEntryVersion: string;
  title: string;
  dueDate: string;
  /** `true` = Datum aus dem Text, `false` = heute (kein Datum erkannt). */
  dueRecognized: boolean;
  priority: QuickEntryPriority;
  /** Erste erwähnte Person im Text, sonst `self`. */
  assigneeId: string | null;
  /** `true` = aus einer Erwähnung, `false` = man selbst. */
  assigneeFromMention: boolean;
  /** Erkannte Teile des Notiztexts (Positionen im Originaltext; volle Namen als `person`). */
  tokens: QuickEntryToken[];
}

/** Volle „@Anzeigename“ finden (danach kein Buchstabe und keine Ziffer); längere Namen zuerst. */
function blankFullMentions(body: string, people: readonly QuickEntryPerson[]): { text: string; hits: { id: string; start: number; end: number }[] } {
  let text = body;
  const hits: { id: string; start: number; end: number }[] = [];
  for (const p of [...people].sort((a, b) => b.displayName.length - a.displayName.length)) {
    const needle = `@${p.displayName}`;
    if (p.displayName.trim() === '') continue;
    for (let i = text.indexOf(needle); i >= 0; i = text.indexOf(needle, i + 1)) {
      const next = text.charAt(i + needle.length);
      if (next !== '' && /[\p{L}\p{N}]/u.test(next)) continue;
      hits.push({ id: p.id, start: i, end: i + needle.length });
      text = text.slice(0, i) + ' '.repeat(needle.length) + text.slice(i + needle.length);
    }
  }
  return { text, hits };
}

function shorten(title: string): string {
  if (title.length <= NOTE_TASK_TITLE_MAX) return title;
  return `${title.slice(0, NOTE_TASK_TITLE_MAX - 1).trimEnd()}…`;
}

/** Entwurf einer Aufgabe aus dem Text einer Notiz (siehe Kopf). */
export function buildNoteTaskDraft(input: NoteTaskDraftInput): NoteTaskDraft {
  const { body, today, mentioned, self } = input;
  const blanked = blankFullMentions(body, mentioned);
  const parsed = parseTaskQuickEntry(blanked.text, today, { people: mentioned, labels: [], projects: [] }, { records: false, ignore: ['label', 'project'] });

  const fullTokens: QuickEntryToken[] = blanked.hits.map((h) => ({ kind: 'person', start: h.start, end: h.end, text: body.slice(h.start, h.end) }));
  const tokens = [...parsed.tokens, ...fullTokens].sort((a, b) => a.start - b.start);

  // Zuständig: erste Erwähnung im Text (voller Name oder eindeutige Kurzform).
  const candidates = blanked.hits.map((h) => ({ id: h.id, start: h.start }));
  if (parsed.person !== null && !isAmbiguous(parsed.person)) {
    const token = parsed.tokens.find((t) => t.kind === 'person');
    if (token) candidates.push({ id: parsed.person.id, start: token.start });
  }
  candidates.sort((a, b) => a.start - b.start);
  const first = candidates[0];

  // Titel: erste nicht leere Zeile ohne erkannte Teile.
  const lineStart = (() => {
    let pos = 0;
    for (const line of body.split('\n')) {
      if (line.trim() !== '') return { start: pos, end: pos + line.length };
      pos += line.length + 1;
    }
    return { start: 0, end: body.length };
  })();
  let line = '';
  for (let i = lineStart.start; i < lineStart.end; i += 1) {
    line += tokens.some((t) => i >= t.start && i < t.end) ? ' ' : body.charAt(i);
  }
  const squash = (s: string) => s.replace(/\s+/g, ' ').trim();
  const title = squash(line) || squash(body.slice(lineStart.start, lineStart.end));

  return {
    version: NOTE_TASK_VERSION,
    quickEntryVersion: TASK_QUICK_ENTRY_VERSION,
    title: shorten(title),
    dueDate: parsed.dueDate ?? today,
    dueRecognized: parsed.dueDate !== null,
    priority: parsed.priority ?? 'normal',
    assigneeId: first ? first.id : self,
    assigneeFromMention: first !== undefined,
    tokens,
  };
}
